import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, forkJoin, of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { BancoldexService } from '../../../comun/services/bancoldex.service';
import { UserService } from '../../../services/user.service';
import { isPerfilJuridica } from '../perfil-tipo.util';
import {
  PERFIL_PAR,
  PERFIL_SEGMENTO_PARAMS_FALLBACK,
  PerfilSegmento,
  PerfilSegmentoParams,
  PerfilSolicitaCreditoDraft,
  parseMontoDigits,
  parsePlazoNumber,
  readSolicitaCreditoDraft,
  resolveMuestraFlujoCaja,
  resolveSegmento,
  resolveTipoLinea,
  writeSolicitaCreditoDraft,
} from './perfil-segmento.util';

interface ParametroFilterRow {
  valorParametro?: string;
}

@Injectable({ providedIn: 'root' })
export class PerfilSegmentoService {
  private params: PerfilSegmentoParams = { ...PERFIL_SEGMENTO_PARAMS_FALLBACK };
  private paramsLoaded = false;
  private readonly segmentoSubject = new BehaviorSubject<PerfilSegmento>(
    this.readSegmentoFromDraft()
  );

  readonly segmento$: Observable<PerfilSegmento> =
    this.segmentoSubject.asObservable();

  constructor(
    private readonly bancoldex: BancoldexService,
    private readonly userService: UserService
  ) {}

  get segmento(): PerfilSegmento {
    return this.segmentoSubject.value;
  }

  get esExpress(): boolean {
    if (this.esJuridica) {
      return false;
    }
    return this.segmento === 'express';
  }

  get paramsSnapshot(): Readonly<PerfilSegmentoParams> {
    return this.params;
  }

  get muestraFlujoCaja(): boolean {
    if (this.esJuridica) {
      return false;
    }
    const draft = readSolicitaCreditoDraft();
    if (typeof draft?.muestraFlujoCaja === 'boolean') {
      return draft.muestraFlujoCaja;
    }
    return resolveMuestraFlujoCaja(
      this.segmento,
      this.params.seccionFlujoCaja
    );
  }

  private get esJuridica(): boolean {
    return isPerfilJuridica(this.userService.getUser()?.tipoPersona);
  }

  ensureParamsLoaded(): Observable<PerfilSegmentoParams> {
    if (this.paramsLoaded) {
      return of(this.params);
    }
    return this.loadParams();
  }

  loadParams(): Observable<PerfilSegmentoParams> {
    const ids = [
      PERFIL_PAR.montoMaximoGlobal,
      PERFIL_PAR.montoMaxExpress,
      PERFIL_PAR.montoMinExpress,
      PERFIL_PAR.plazoMaxExpress,
      PERFIL_PAR.plazoMinExpress,
      PERFIL_PAR.seccionFlujoCaja,
      PERFIL_PAR.fechaRegistroMinimaMeses,
      PERFIL_PAR.textoMonto,
      PERFIL_PAR.textoPlazo,
      PERFIL_PAR.textoFechaRegistro,
      PERFIL_PAR.tiempoMinimoDeclaracionRenta,
      PERFIL_PAR.valorLimiteVentas,
    ] as const;

    const requests = ids.map((idParametro) =>
      this.bancoldex
        .getFilterParametro({
          idParametro,
          nombre: null,
          isParFuncional: true,
          pagina: 1,
        })
        .pipe(
          map((response: { data?: ParametroFilterRow[] }) => {
            const row = response?.data?.[0];
            return { id: idParametro, valor: row?.valorParametro ?? '' };
          }),
          catchError(() => of({ id: idParametro, valor: '' }))
        )
    );

    return forkJoin(requests).pipe(
      map((rows) => {
        const byId = Object.fromEntries(
          rows.map((r) => [r.id, r.valor])
        ) as Record<string, string>;
        const next: PerfilSegmentoParams = {
          ...PERFIL_SEGMENTO_PARAMS_FALLBACK,
          montoMaximoGlobal: this.toNumber(
            byId[PERFIL_PAR.montoMaximoGlobal],
            PERFIL_SEGMENTO_PARAMS_FALLBACK.montoMaximoGlobal
          ),
          montoMaxExpress: this.toNumber(
            byId[PERFIL_PAR.montoMaxExpress],
            PERFIL_SEGMENTO_PARAMS_FALLBACK.montoMaxExpress
          ),
          montoMinExpress: this.toNumber(
            byId[PERFIL_PAR.montoMinExpress],
            PERFIL_SEGMENTO_PARAMS_FALLBACK.montoMinExpress
          ),
          plazoMaxExpress: this.toNumber(
            byId[PERFIL_PAR.plazoMaxExpress],
            PERFIL_SEGMENTO_PARAMS_FALLBACK.plazoMaxExpress
          ),
          plazoMinExpress: this.toNumber(
            byId[PERFIL_PAR.plazoMinExpress],
            PERFIL_SEGMENTO_PARAMS_FALLBACK.plazoMinExpress
          ),
          fechaRegistroMinimaMeses: this.toNumber(
            byId[PERFIL_PAR.fechaRegistroMinimaMeses],
            PERFIL_SEGMENTO_PARAMS_FALLBACK.fechaRegistroMinimaMeses
          ),
          seccionFlujoCaja: this.toBool(
            byId[PERFIL_PAR.seccionFlujoCaja],
            PERFIL_SEGMENTO_PARAMS_FALLBACK.seccionFlujoCaja
          ),
          tiempoMinimoDeclaracionRenta: this.toNumberOrNull(
            byId[PERFIL_PAR.tiempoMinimoDeclaracionRenta]
          ),
          valorLimiteVentas: this.toNumberOrNull(
            byId[PERFIL_PAR.valorLimiteVentas]
          ),
          mensajeMonto: byId[PERFIL_PAR.textoMonto] || undefined,
          mensajePlazo: byId[PERFIL_PAR.textoPlazo] || undefined,
          mensajeFechaRegistro: byId[PERFIL_PAR.textoFechaRegistro] || undefined,
        };
        this.params = next;
        this.paramsLoaded = true;
        return next;
      }),
      catchError(() => {
        this.params = { ...PERFIL_SEGMENTO_PARAMS_FALLBACK };
        this.paramsLoaded = true;
        return of(this.params);
      }),
      tap(() => {
        // Re-sincroniza flag de flujo si ya hay draft.
        const draft = readSolicitaCreditoDraft();
        if (this.esJuridica) {
          if (
            draft?.segmento !== 'tradicional' ||
            draft?.muestraFlujoCaja !== false
          ) {
            writeSolicitaCreditoDraft({
              ...draft,
              segmento: 'tradicional',
              muestraFlujoCaja: false,
            });
          }
          this.segmentoSubject.next('tradicional');
          return;
        }
        if (draft?.segmento) {
          const muestra = resolveMuestraFlujoCaja(
            draft.segmento,
            this.params.seccionFlujoCaja
          );
          if (draft.muestraFlujoCaja !== muestra) {
            writeSolicitaCreditoDraft({ ...draft, muestraFlujoCaja: muestra });
          }
        }
      })
    );
  }

  /**
   * Calcula segmento desde monto/plazo, persiste en draft y notifica.
   */
  applyFromMontoPlazo(
    montoRaw: unknown,
    plazoRaw: unknown,
    extraDraft: Partial<PerfilSolicitaCreditoDraft> = {}
  ): PerfilSegmento {
    const monto = parseMontoDigits(montoRaw);
    const plazo = parsePlazoNumber(plazoRaw);
    const segmento: PerfilSegmento = this.esJuridica
      ? 'tradicional'
      : resolveSegmento(monto, plazo, this.params);
    const muestraFlujoCaja = this.esJuridica
      ? false
      : resolveMuestraFlujoCaja(segmento, this.params.seccionFlujoCaja);
    const prev = readSolicitaCreditoDraft() ?? {};
    const tipoCredito = (extraDraft.tipoCredito ?? prev.tipoCredito) as
      | { bancoldex?: boolean; aliados?: boolean; ambas?: boolean }
      | undefined;
    writeSolicitaCreditoDraft({
      ...prev,
      ...extraDraft,
      monto: String(montoRaw ?? prev.monto ?? ''),
      plazo: String(plazoRaw ?? prev.plazo ?? ''),
      segmento,
      muestraFlujoCaja,
      tipoLinea:
        extraDraft.tipoLinea ??
        resolveTipoLinea(tipoCredito) ??
        prev.tipoLinea ??
        null,
    });
    this.segmentoSubject.next(segmento);
    return segmento;
  }

  refreshFromDraft(): PerfilSegmento {
    const segmento = this.readSegmentoFromDraft();
    this.segmentoSubject.next(segmento);
    return segmento;
  }

  private readSegmentoFromDraft(): PerfilSegmento {
    if (this.esJuridica) {
      return 'tradicional';
    }
    const draft = readSolicitaCreditoDraft();
    if (draft?.segmento === 'express' || draft?.segmento === 'tradicional') {
      return draft.segmento;
    }
    if (draft?.monto != null && draft?.plazo != null) {
      return resolveSegmento(
        parseMontoDigits(draft.monto),
        parsePlazoNumber(draft.plazo),
        this.params
      );
    }
    return 'tradicional';
  }

  private toNumber(raw: string, fallback: number): number {
    if (raw == null || raw === '') {
      return fallback;
    }
    const n = Number(String(raw).replace(/,/g, ''));
    return Number.isFinite(n) ? n : fallback;
  }

  private toNumberOrNull(raw: string): number | null {
    if (raw == null || raw === '') {
      return null;
    }
    const n = Number(String(raw).replace(/,/g, ''));
    return Number.isFinite(n) ? n : null;
  }

  private toBool(raw: string, fallback: boolean): boolean {
    if (raw == null || raw === '') {
      return fallback;
    }
    try {
      return JSON.parse(String(raw).toLowerCase()) === true;
    } catch {
      return String(raw).toLowerCase() === 'true' || String(raw) === '1'
        ? true
        : fallback;
    }
  }
}
